import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from "react-redux";
import { findUser, findDevice } from '../_actions/user_action';
import setting from '../config/setting';

import { CustomOverlayMap, Map, MapMarker } from 'react-kakao-maps-sdk';

import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import '../css_admin/Overlay.css';

import MARKER_SRC_ONLINE from '../components/images/marker/marker_online.png';
import MARKER_SRC_OFFLINE from '../components/images/marker/marker_offline.png';
import MARKER_SRC_ERROR from '../components/images/marker/marker_error.png';
import MARKER_SRC_READY from '../components/images/marker/marker_ready.png';
import { getUserAccount } from '../utils/session';

const key = require('../config/key');

const MapContainer = (props) => {
    const dispatch = useDispatch();

    // 1) userInfo 상태
    const [userInfo, setUserInfo] = useState(null);
    const [overlayInfoList, setOverlayInfoList] = useState([]);
    const [isReady, setIsReady] = useState(false);

    // 열린 Overlay ID 관리
    const [activeOverlayId, setActiveOverlayId] = useState(null);

    // 중복 호출 방지, 타이머 ref
    const isFetchingRef = useRef(false);
    const timerRef = useRef(null);

    // 2) 마운트 시 userInfo fetch
    useEffect(() => {
        const account = getUserAccount();
        if (!account) {
            console.warn("userAccount 무효 - RequireAuth 가 처리");
            return;
        }
        const email = account.email;

        dispatch(findUser({ email })).then(res => {
            if (res.payload.findSuccess) {
                setUserInfo(res.payload.userInfo);
            } else {
                console.error("findUser 실패");
            }
        })
        .catch(err => console.error("findUser 에러", err));
    }, [dispatch]);
    
    // 3) 지도 데이터 로드 함수
    const displayMap = useCallback(async () => {
        // 3-1) 중복 호출 방지
        if (isFetchingRef.current) return;
        // 3-2) userInfo 준비 확인
        if (!userInfo?.sensorNode?.length) {
            console.warn("displayMap: sensorNode 없음 또는 빈 배열");
            return;
        }
        isFetchingRef.current = true;
    
        const updatedOverlayInfoList = [];
        for (const sensorNode of userInfo.sensorNode) {
            const body = {
                id: userInfo.email,
                sensorNumber: sensorNode.value
            };

            try {
                // console.log('findDevice:', body);
                const response = await dispatch(findDevice(body));
                if (response.payload.findSuccess) {
                    console.log('findDevice:', response);
                    const device = response.payload.deviceInfo;
                    // 마커 이미지 & 텍스트 결정
                    let markerImage = MARKER_SRC_READY, stateText = "대기상태";
                    if (device.state === "Online") {
                        markerImage = MARKER_SRC_ONLINE; stateText = "온라인-정상동작";
                    } else if (device.state === "Offline") {
                        markerImage = MARKER_SRC_OFFLINE; stateText = "오프라인-점검필요";
                    } else if (device.state === "Error") {
                        markerImage = MARKER_SRC_ERROR; stateText = "데이터 에러-통신오류";
                    }
                    // 위치 & 주소
                    const deviceLat = parseFloat(device.latitude) || parseFloat(userInfo.latitude);
                    const deviceLng = parseFloat(device.longitude) || parseFloat(userInfo.longitude);
                    const address = await getAddressFromCoords(deviceLat, deviceLng);
                    // 센서 ID 마스킹
                    const sensorId = device.sensorId;
                    const maskedSensorId = '*'.repeat(sensorId.length - 4) + sensorId.slice(-4);
                    let info = {
                        mID: device.id,
                        mLat: deviceLat,
                        mLng: deviceLng,
                        mAddress: address,
                        mSensor: device.sensorNumber,
                        mSensorId: maskedSensorId,
                        mState: device.state,
                        mStateText: stateText,
                        mMarkerImage: markerImage
                    };
                    // console.log('info:', info);
                    updatedOverlayInfoList.push(info);
                }
            } catch (error) {
                console.error("장치 데이터 조회 오류:", error);
            }
        }

        // setOverlayInfoList(updatedOverlayInfoList);
        setOverlayInfoList(spreadOverlappingMarkers(updatedOverlayInfoList));
        setIsReady(true);
        isFetchingRef.current = false;
    }, [dispatch, userInfo]);

    // 4) userInfo가 준비되면 displayMap 호출 & 타이머 설정
    useEffect(() => {
        if (!userInfo?.sensorNode?.length) return;

        // 즉시 한 번
        displayMap();
        // 설정된 분마다 반복
        const termMs = Number(setting.graph_refresh_term) * 60 * 1000;
        timerRef.current = setInterval(displayMap, termMs);

        return () => clearInterval(timerRef.current);
    }, [userInfo, displayMap]);
    
    // 5) 주소 변환 헬퍼
    const getAddressFromCoords = async (lat, lng) => {
        try {
            const response = await fetch(
                `https://dapi.kakao.com/v2/local/geo/coord2address.json?x=${lng}&y=${lat}&input_coord=WGS84`,
                {
                    method: "GET",
                    headers: {
                        "Authorization": `KakaoAK ${key.KAKAO_APP_KEY}`
                    }
                }
            );

            const data = await response.json();
            if (data.documents && data.documents.length > 0) {
                return data.documents[0].address.address_name;
            }
        } catch (error) {
            console.error("주소 변환 오류:", error);
        }
        return "";
    };

    // MapContainer Marker Spread
    // 1) 겹치는 마커 좌표를 작은 원으로 퍼뜨려 주는 헬퍼
    function spreadOverlappingMarkers(list) {
        // 1-1) 같은 좌표끼리 그룹핑
        const groups = list.reduce((acc, info) => {
            const key = `${info.mLat}_${info.mLng}`;
            (acc[key] = acc[key] || []).push(info);
            return acc;
        }, {});
            
        // 1-2) 그룹별로 퍼뜨리기
        const result = [];
        const radius = 0.0001; // 약 11m
        Object.values(groups).forEach(group => {
            if (group.length === 1) {
                result.push(group[0]);
            } else {
                const angleStep = (2 * Math.PI) / group.length;
                group.forEach((info, idx) => {
                    const angle = angleStep * idx;
                    const lat = parseFloat(info.mLat) + radius * Math.cos(angle);
                    const lng = parseFloat(info.mLng) + radius * Math.sin(angle);
                    result.push({ ...info, mLat: lat, mLng: lng });
                });
            }
        });
        return result;
    }
    
    return (
        <div className="mapContainer">
            {isReady &&
                <Map
                    center={{
                        lat: setting.map_center_latitude,
                        lng: setting.map_center_longitude
                    }}
                    className="mapResponsive"
                    level={setting.map_level}
                    onClick={() => setActiveOverlayId(null)}
                >
                    {
                        overlayInfoList.map((overlayInfo, index) => (
                            <EventOverlayContainer
                                key={`EventMarker-${overlayInfo.mSensor}`}
                                position={{ lat: overlayInfo.mLat, lng: overlayInfo.mLng }}
                                info={overlayInfo}
                                // 상위에서 관리하는 열린 Overlay ID와 setter 전달
                                activeOverlayId={activeOverlayId}
                                setActiveOverlayId={setActiveOverlayId}
                            />
                        ))
                    }
                </Map>
            }
        </div>
    );        
};

const EventOverlayContainer = ({ position, info, activeOverlayId, setActiveOverlayId }) => {
    const isVisible = activeOverlayId === info.mSensor;
    // console.log('isVisible:', activeOverlayId, info.mSensor);

    const totalAddress = info.mAddress;
    const arrayAddress = totalAddress.split(" ");
    let line1Address = arrayAddress[0] + " " + arrayAddress[1] + " " + arrayAddress[2];
    let line2Address = totalAddress.substring(line1Address.length);
    let line3Address = "";
    if (line2Address.includes('(') && line2Address.includes(')')) {
        const partAddress = line2Address;
        const arrayPart = partAddress.split("(");
        line2Address = arrayPart[0];
        line3Address = partAddress.substring(line2Address.length);
    }

    const handleMarkerClick = () => {
        setActiveOverlayId(prev => prev === info.mSensor ? null : info.mSensor);
    };

    return (
        <>
        <MapMarker
            position={position}
            image={{
                src: info.mMarkerImage,
                size: { width: 20, height: 30 },
                options: { offset: { x: 18, y: 36 } }
            }}
            onClick={handleMarkerClick}
        />
        <CustomOverlayMap position={position}>
            {isVisible && (
                <div className="mapMarkerPopup" style={{ height: '240px' }}>
                    <div className="mapMarkerPopupLayer">
                    <div className="modalCloseIcon">
                        <div
                            className="modalCloseIconImg"
                            onClick={() => setActiveOverlayId(null)}
                        />
                        </div>
                        <h2>{info.mSensor}</h2>
                        <div className="markerText">
                            <FontAwesomeIcon
                                icon={faLocationDot}
                                style={{
                                    color:
                                        info.mState === "Online" ? '#75C03A' :
                                        info.mState === "Offline" ? '#DB2F18' :
                                        info.mState === "Error" ? '#F29729' :
                                        '#BABABA',
                                    fontSize: '14px'
                                }}
                            />
                            &nbsp;<span>{info.mStateText}</span>
                        </div>
                        <hr />
                        <div className="PopuptextareaId">
                            <div className="PopuptextareaIdTitle">ID : </div>
                            <div className="PopuptextareaIdText">{info.mSensorId}</div>
                        </div>
                        <div className="PopuptextareaLocation">
                            <div className="PopuptextareaIdTitle">Location : </div>
                            <div className="PopuptextareaIdText">{line1Address}</div>
                            <div className="PopuptextareaIdText">{line2Address}</div>
                            <div className="PopuptextareaIdText">{line3Address}</div>
                        </div>
                    </div>
                </div>
                )
            }
        </CustomOverlayMap>
        </>
    );
};

export default MapContainer;
